import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { login } from '../services/api';
import SecureLS from 'secure-ls';
import { setUserFirstName } from '../utils/userUtils';
import { setUser, setFamilies } from '../utils/secureStorage';
import { useFamily } from '../contexts/FamilyContext';

const ls = new SecureLS({ encodingType: 'aes' });

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null); // { code, message, attemptsRemaining, email, retryAt }
  const navigate = useNavigate();
  const { updateFamilies } = useFamily();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear any existing errors

    try {
      const userData = await login({ email, password });
      
      // Store user data securely (encrypted)
      setUser({
        id: userData.user.id,
        person_id: userData.user.person_id,
        email: userData.user.email,
        phone: userData.user.phone,
        first_name: userData.user.first_name,
        last_name: userData.user.last_name,
        age: userData.user.age,
        date_of_birth: userData.user.date_of_birth,
      })

      // Store families information and set active family
      if (userData.families && userData.families.length > 0) {
        const primaryId = userData.primary_family_id || userData.families[0].id;
        setFamilies(userData.families);
        localStorage.setItem('primaryFamilyId', primaryId);

        // Use primary_family_id if present, otherwise keep existing or use first
        if (userData.primary_family_id) {
          localStorage.setItem('familyId', userData.primary_family_id);
        } else if (!localStorage.getItem('familyId')) {
          localStorage.setItem('familyId', userData.families[0].id);
        }

        // Update FamilyContext
        updateFamilies(userData.families, primaryId);
      }

      // Store user's first name for personalized greetings
      setUserFirstName(userData.user.first_name);

      // Store sensitive data using secure-ls
      ls.set('authToken', userData.token);
      localStorage.setItem('isLoggedIn', 'true');

      // If login is successful, navigate to My Life page
      // (will show onboarding if not completed, or chapters if completed)
      navigate('/my-life');
    } catch (err) {
      const data = err.response?.data || {};
      const code = data.code || (err.response ? 'invalid_credentials' : 'network_error');
      setError({
        code,
        message: data.error || err.message || 'Something went wrong.',
        attemptsRemaining: data.attempts_remaining,
        email: data.email,
        retryAt: data.retry_at,
        retryAfter: data.retry_after,
        restorePath: data.restore_path,
      });
      console.error('Login error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-300">
          {/* Header */}
          <div className="bg-[#556B2F] text-white p-6 text-center">
            <h1 className="text-2xl font-bold mb-2">Welcome Back</h1>
            <p className="text-base opacity-90">Sign in to your family's story collection</p>
          </div>
          
          {/* Content */}
          <div className="p-8">
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (() => {
            const isLocked = error.code === 'account_locked';
            const isUnverified = error.code === 'email_not_verified';
            const isDeletionPending = error.code === 'deletion_pending';
            const isNetwork = error.code === 'network_error';
            const bg = isUnverified ? '#FFF6E6' : isNetwork ? '#F1F3EA' : '#FDECEA';
            const border = isUnverified ? '#F1DCB0' : isNetwork ? '#D8DCC8' : '#F0B4AC';
            const rule = isUnverified ? '#B5760A' : isNetwork ? '#8A917A' : '#C0392B';
            const textColor = isUnverified ? '#7A5C00' : isNetwork ? '#5A6150' : '#8B1A1A';
            return (
              <div role="alert" style={{
                background: bg, border: `1.5px solid ${border}`, borderLeft: `5px solid ${rule}`,
                borderRadius: 12, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                <div style={{ flexShrink: 0, marginTop: 2, color: rule }}>
                  {isNetwork ? '📡' : isUnverified ? '✉️' : '⚠️'}
                </div>
                <div style={{ flex: 1, fontFamily: "'Source Sans 3', sans-serif" }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: textColor, marginBottom: 4 }}>
                    {isDeletionPending ? 'Your account is closing' : isLocked ? 'Sign-in paused' : isUnverified ? 'Please confirm your email first' : isNetwork ? "We couldn't reach 1G2N" : "That email and password don't match"}
                  </div>
                  <div style={{ fontSize: 14.5, color: textColor, opacity: .85 }}>
                    {isLocked && error.retryAt && `Try again at ${new Date(error.retryAt).toLocaleTimeString()}. `}
                    {isLocked && <Link to="/forgot-password" style={{ color: rule, fontWeight: 600 }}>Reset your password now</Link>}
                    {error.code === 'invalid_credentials' && (
                      <>
                        Check the spelling of your email and password.
                        {error.attemptsRemaining != null && <span style={{ display: 'block', marginTop: 4, fontWeight: 600 }}>{error.attemptsRemaining} attempt{error.attemptsRemaining !== 1 ? 's' : ''} left before we pause sign-in for 15 minutes.</span>}
                        <div style={{ marginTop: 6 }}><Link to="/forgot-password" style={{ color: rule, fontWeight: 600 }}>Forgot your password?</Link></div>
                      </>
                    )}
                    {isUnverified && (
                      <>We sent a confirmation to <b>{error.email}</b>. Check your inbox.</>
                    )}
                    {isDeletionPending && (
                      <>
                        Your account is scheduled for deletion. <Link to={error.restorePath || '/settings'} style={{ color: '#B5760A', fontWeight: 600 }}>Bring my account back</Link>
                      </>
                    )}
                    {isNetwork && 'Check your connection and try again — nothing was lost.'}
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="space-y-6">
            <div>
              <label htmlFor="email-address" className="block text-base font-bold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-base font-bold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none pr-12"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="w-full bg-[#556B2F] text-white border-none py-4 px-4 text-lg font-bold rounded-lg cursor-pointer transition-colors hover:bg-[#4A5D23]"
            >
              Log In
            </button>
          </div>
        </form>

        <div className="text-sm text-center mt-4">
          <Link to="/forgot-password" className="font-medium text-[#556B2F] hover:underline">
            Forgot your password?
          </Link>
        </div>

        <div className="text-sm text-center mt-4">
          <span>Don't have an account? </span>
          <Link to="/auth/register" className="font-medium text-[#556B2F] hover:underline">
            Sign Up
          </Link>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
